/**
 * Anonymous first-party funnel key (#148 D148.4/D148.9, NFR-6.6).
 *
 * A persistent, opaque `localStorage` UUID minted once on first page load and reused
 * thereafter. It is the HEAD of the adoption funnel: the anonymous `landed` stage carries
 * it, and it is threaded onto the identity/billing/game control-plane POSTs so the
 * server-authoritative funnel log line can join the pre-signup journey to the guest /
 * registered player id.
 *
 * Privacy (owner-signed D148.9): this is a first-party opaque UUID, **never PII**, and
 * **not a cookie** (no cross-site propagation). Funnel telemetry keyed by it is
 * retention-aged, not point-deleted (D148.5).
 *
 * Degrade-safe: `localStorage` can throw (private mode, disabled storage, SSR). Any
 * failure falls back to a per-load in-memory key so callers always get a usable id and
 * telemetry NEVER breaks the page (the same no-throw discipline as `rum.ts`).
 */

const STORAGE_KEY = 'jeopardy.funnelKey';

/** In-memory fallback when `localStorage` is unavailable, stable within a page load. */
let memoryKey: string | null = null;

/** Mint a UUID, falling back to a non-crypto id if `crypto.randomUUID` is unavailable. */
function mintKey(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // fall through to the non-crypto fallback
  }
  return `f-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Read the persistent anonymous funnel key, minting + persisting it on first use.
 * Memoized within a load via the in-memory fallback, so repeated calls return the same
 * value even when `localStorage` is unavailable.
 */
export function getFunnelKey(): string {
  try {
    const existing = window.localStorage.getItem(STORAGE_KEY);
    if (existing) return existing;
    const minted = mintKey();
    window.localStorage.setItem(STORAGE_KEY, minted);
    return minted;
  } catch {
    // localStorage unavailable (private mode / disabled / SSR) — use a stable per-load
    // in-memory key so the funnel still has a head, without ever throwing.
    memoryKey ??= mintKey();
    return memoryKey;
  }
}

/** Test-only: drop the in-memory fallback so each test starts clean. */
export function resetFunnelKeyForTests(): void {
  memoryKey = null;
}
